/**
 * Executor action registry and action payloads (specs/c2-kontratlar.md 5.2 and 5.10).
 *
 * twin.action_types (0014) is the single source of tier, executor mode, platform and
 * undo window; this registry adds what code needs: the handler, the panel texts and
 * card kind, the "Düzelt" field and the payload schema. A test compares the registry
 * with the database table: the registry holds no action type the database lacks.
 *
 * The same payload schema is used by propose_action (tools), by the API when Sercan
 * edits, and by the executor before it applies. All payload schemas are strict.
 */
import { z } from 'zod';
import { idStringSchema, isoDateSchema } from './ids.js';
// ---------------------------------------------------------------------------
// Payload building blocks
// ---------------------------------------------------------------------------
/** Etsy listing ids are numeric strings. */
export const listingIdSchema = z.string().regex(/^\d{1,20}$/, { error: 'listing id must be digits' });
/** Etsy limits: title at most 140 characters, at most 13 tags of at most 20 characters. */
export const ETSY_TITLE_MAX = 140;
export const ETSY_TAGS_MAX = 13;
export const ETSY_TAG_MAX = 20;
export const ETSY_DESCRIPTION_MAX = 20_000;
const title = z.string().min(1).max(ETSY_TITLE_MAX);
const tags = z.array(z.string().min(1).max(ETSY_TAG_MAX)).max(ETSY_TAGS_MAX);
const description = z.string().max(ETSY_DESCRIPTION_MAX);
const textTr = z.string().min(1).max(2000);
const LINK_TLDS = [
    'com',
    'net',
    'org',
    'io',
    'co',
    'app',
    'dev',
    'info',
    'biz',
    'shop',
    'store',
    'online',
    'site',
    'xyz',
    'me',
    'ly',
    'link',
    'page',
    'ai',
    'us',
    'uk',
    'au',
    'ca',
    'nz',
    'za',
    'br',
    'tr',
    'de',
    'fr',
    'eu',
    'pro',
    'tech',
    'art',
    'design',
    'studio',
    'club',
    'live',
    'top',
    'in',
    'es',
    'it',
    'nl',
    'jp',
    'cn',
    'ru',
    'gg',
    'tv',
    'to',
    'cc',
];
const LINK_PATTERNS = [
    /\bhttps?:\/\//i,
    /\bwww\./i,
    /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/,
    new RegExp(`\\b[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*\\.(?:${LINK_TLDS.join('|')})\\b`, 'i'),
];
/** "example dot com", "example (dot) net", "example(.)org", "example[.]biz". */
const SPELLED_DOMAIN = new RegExp(`\\b[a-z0-9-]{2,}\\s*(?:\\(\\s*(?:dot|\\.)\\s*\\)|\\[\\s*(?:dot|\\.)\\s*\\]|\\bdot\\b)\\s*(?:${['com', 'net', 'org', 'io', 'co', 'shop', 'store', 'info'].join('|')})\\b`, 'i');
/**
 * Our own Etsy links (v0.1.47): a listing or the shop page. Sercan, 2026-09-29: "satın alma
 * linklerini de eklememiz lazım cevaplara". They are on Etsy, so an Etsy message may carry them,
 * and an e-mail reply needs them. Every other URL, address or domain stays forbidden.
 */
export const OWN_ETSY_LINK = /https?:\/\/(?:(?:www\.)?etsy\.com\/(?:listing\/\d{6,}(?:[/?#][^\s<>"')\]]*)?|shop\/CadGaragePro\/?)|cadgaragepro\.etsy\.com(?:\/listing\/\d{6,}(?:[/?#][^\s<>"')\]]*)?|\/?(?=[\s<>"')\]]|[,.!?;:](?![\w-])|$)))/gi;
/**
 * v0.1.226 (Etsy Share & Save, 22 Oct 2026): links Twin writes go through the shop's own Etsy address. Etsy answers it
 * with etsrc=sdt and refunds part of the fees on the orders such a link brings (6.5% until 4 Jan 2027, then 4%).
 */
export const ETSY_SHARE_BASE = 'https://cadgaragepro.etsy.com';
/** Our listing's Share & Save link, built from its id (Etsy's own url carries the title as a slug). */
export function shareListingUrl(listingId) {
    return `${ETSY_SHARE_BASE}/listing/${listingId}`;
}
/** The text with our own Etsy links taken out, for the link rule. */
export function withoutOwnEtsyLinks(text) {
    return text.replace(OWN_ETSY_LINK, ' ');
}
/**
 * True when text holds a URL, an e-mail address or a domain name
 * (policies.yaml etsy.no_offsite_links_in_etsy_messages). Deliberately strict: a
 * rejected draft is rewritten, a link in an Etsy message breaks Etsy's rules.
 */
export function containsLinkOrDomain(text) {
    const visible = visibleText(text);
    return [text, visible].some((value) => LINK_PATTERNS.some((pattern) => pattern.test(value)) || SPELLED_DOMAIN.test(value));
}
/**
 * The text as a reader sees it: compatibility forms folded (full-width dots and letters),
 * invisible format characters (zero-width space and joiners) removed, the ideographic
 * full stop read as a dot.
 */
function visibleText(text) {
    return text
        .normalize('NFKC')
        .replace(/\p{Cf}/gu, '')
        .replace(/\u3002/g, '.');
}
const replyEn = z
    .string()
    .min(1)
    .max(4000)
    .refine((value) => !containsLinkOrDomain(withoutOwnEtsyLinks(value)), {
    error: 'reply_en must not contain a URL, e-mail address or domain name other than our own Etsy listing (Etsy messages)',
});
// ---------------------------------------------------------------------------
// Payload schemas (5.10)
// ---------------------------------------------------------------------------
export const customerReplyPayloadSchema = z.strictObject({
    conversation_id: idStringSchema,
    /** The only text that goes to the customer. */
    reply_en: replyEn,
    /** Translation for Sercan; null after "Düzelt" (edited_by_human true). */
    reply_tr: z.string().min(1).max(4000).nullable(),
    edited_by_human: z.boolean().optional(),
    /** Short quote of the customer's question (redacted after 180 days). */
    quote_en: z.string().max(500),
    /** v0.1.42: the Temsilci's Turkish rendering of the customer's message, for Sercan's card. */
    quote_tr: z.string().max(1500).optional(),
    country_code: z
        .string()
        .regex(/^[A-Z]{2}$/)
        .nullable(),
});
/**
 * v0.1.43: the Teslimat desk's message for an Etsy order. The download link is Sercan's: the
 * text carries the placeholder [LINK] and, like a customer reply, never a URL or a domain.
 */
export const deliveryMessagePayloadSchema = z.strictObject({
    order_id: z.string().regex(/^[1-9]\d{0,17}$/),
    reply_en: replyEn,
    reply_tr: z.string().min(1).max(4000).nullable(),
    edited_by_human: z.boolean().optional(),
    items: z
        .array(z.strictObject({ title: z.string().min(1).max(300), size_mb: z.number().nonnegative().nullable() }))
        .min(1)
        .max(20),
    country_code: z
        .string()
        .regex(/^[A-Z]{2}$/)
        .nullable(),
});
const listingChangesSchema = z
    .strictObject({
    title: z.strictObject({ before: z.string().max(1000), after: title }).optional(),
    tags: z.strictObject({ before: tags, after: tags.min(1) }).optional(),
    description: z.strictObject({ before: description, after: description.min(1) }).optional(),
})
    .refine((changes) => Object.keys(changes).length > 0, { error: 'changes needs at least one field' });
export const listingTextUpdatePayloadSchema = z.strictObject({
    listing_id: listingIdSchema,
    product_id: idStringSchema,
    changes: listingChangesSchema,
    rationale_tr: textTr,
});
/** v0.1.223: a new listing's description at most 6000 characters (Etsy allows more; our drafts stay readable). */
export const LISTING_NEW_BODY_MAX = 6000;
/**
 * v0.1.223 (Sercan, 22 Oct 2026: "bunları birine ver ekipten"): the İçerik-SEO draft of a NEW Etsy listing for a
 * product without one. Twin cannot create Etsy listings: Sercan approves, then copies the title, the tags and the
 * description into a new listing himself. product_id, sku and price_cents are set by code from shop.products (the
 * price is the catalog price he approved), never by the model.
 */
export const listingNewDraftPayloadSchema = z.strictObject({
    product_id: z.number().int().positive(),
    sku: z.string().min(1).max(40),
    title_en: title,
    tags: tags.refine((list) => new Set(list.map((tag) => tag.toLowerCase())).size === list.length, {
        error: 'tags must be unique',
    }),
    body_en: z.string().min(1).max(LISTING_NEW_BODY_MAX),
    price_cents: z.number().int().nonnegative().nullable(),
});
/** Built by the API from executions.before_state; never from the client (2.5). */
export const listingRevertPayloadSchema = z.strictObject({
    listing_id: listingIdSchema,
    reverts_approval_id: idStringSchema,
    restore: z
        .strictObject({ title: title.optional(), tags: tags.optional(), description: description.optional() })
        .refine((restore) => Object.keys(restore).length > 0, { error: 'restore needs at least one field' }),
});
export const priceChangePayloadSchema = z
    .strictObject({
    listing_id: listingIdSchema,
    product_id: idStringSchema,
    before_cents: z.number().int().nonnegative(),
    after_cents: z.number().int().positive(),
    currency: z.literal('USD'),
})
    .refine((payload) => payload.before_cents !== payload.after_cents, { error: 'the price does not change' });
export const couponCreatePayloadSchema = z
    .strictObject({
    code: z.string().regex(/^[A-Z0-9]{3,20}$/),
    discount_pct: z.number().int().min(1).max(100),
    starts_on: isoDateSchema,
    ends_on: isoDateSchema,
    scope: z.enum(['shop', 'listings']),
    listing_ids: z.array(listingIdSchema).max(100),
    channel: z.string().min(1).max(100).nullable(),
})
    .superRefine((payload, ctx) => {
    if (payload.ends_on < payload.starts_on) {
        ctx.addIssue({ code: 'custom', path: ['ends_on'], message: 'ends_on is before starts_on' });
    }
    if (payload.scope === 'listings' && payload.listing_ids.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['listing_ids'], message: 'a listings coupon names its listings' });
    }
    if (payload.scope === 'shop' && payload.listing_ids.length > 0) {
        ctx.addIssue({ code: 'custom', path: ['listing_ids'], message: 'a shop coupon has no listing ids' });
    }
});
export const siteMergePayloadSchema = z.strictObject({
    /** owner/name; must equal GITHUB_SITE_REPO (checked by the executor, not by the proposer). */
    repo: z.string().regex(/^[A-Za-z0-9_.-]{1,100}\/[A-Za-z0-9_.-]{1,100}$/),
    pr_number: z.number().int().positive(),
    head_sha: z.string().regex(/^[0-9a-f]{40}$/),
    title: z.string().min(1).max(256),
    summary_tr: textTr,
});
/** The site files Twin may change: the guides (v0.1.192, Sercan 16 Oct 2026: guides only). */
export const SITE_GUIDE_PATH = /^src\/content\/guides\/[a-z0-9]+(?:-[a-z0-9]+){0,12}\.md$/;
export const SITE_CONTENT_MAX = 60_000;
/**
 * Longest new text of one site edit. v0.1.213 (19 Oct 2026: two of three Monday proposals were refused at 300): the
 * skill asks for an answer paragraph of up to 90 words, about 600 characters. The card still shows every new text
 * whole (SITE_PREVIEW_MAX, review of #58 and #82).
 */
export const SITE_REPLACE_MAX = 700;
/** One targeted change of a guide: `find` occurs exactly once in the file; `replace` takes its place. */
export const siteEditSchema = z.strictObject({
    find: z.string().min(8).max(4000),
    replace: z.string().max(SITE_REPLACE_MAX),
});
/**
 * site_publish (v0.1.192, the SEO loop): a guide of cadgaragepro.com changes. The payload carries the edits Sercan
 * reads and the whole new file they produce from the file at base_sha256; the site builder applies it only while the
 * file is still at that hash, builds the site, runs the SEO audit and publishes. Nothing else of the site changes.
 */
export const sitePublishPayloadSchema = z.strictObject({
    path: z.string().regex(SITE_GUIDE_PATH),
    /** sha256 of the file the edits were made against. */
    base_sha256: z.string().regex(/^[0-9a-f]{64}$/),
    edits: z.array(siteEditSchema).min(1).max(3),
    content: z.string().min(200).max(SITE_CONTENT_MAX),
    content_sha256: z.string().regex(/^[0-9a-f]{64}$/),
    /** The page as a visitor opens it. */
    url: z.string().url().max(300),
    summary_tr: textTr,
    /** shop.seo_targets.target_id when the change answers a target of the weekly SEO loop. */
    target_id: z
        .string()
        .regex(/^[0-9]{1,18}$/)
        .optional(),
});
/** custom_quote, b2b_quote, b2b_email, outreach_reply: the recipient is an id, never an address. */
export const emailPayloadSchema = z.strictObject({
    to_ref: z.strictObject({ kind: z.enum(['conversation', 'lead', 'channel']), id: idStringSchema }),
    subject: z.string().min(1).max(200),
    body_text: z.string().min(1).max(10_000),
    thread_ref: z.string().min(1).max(200).nullable(),
    amount_cents: z.number().int().nonnegative().optional(),
});
/**
 * b2b_email (v0.1.137): one outreach draft Sercan approved on Tanıtım gönderimi. The send gate
 * (growth.messages_guard) matches message_idempotency_key and body_hash (growth.message_body_hash of the
 * subject and text, the database's own hash) against the message before it may be scheduled; the
 * recipient is the lead and the channel, never an address.
 */
export const outreachEmailPayloadSchema = z.strictObject({
    to_ref: z.strictObject({ kind: z.literal('lead'), id: idStringSchema }),
    channel_id: idStringSchema,
    message_idempotency_key: z.string().min(8).max(200),
    body_hash: z.string().regex(/^[0-9a-f]{64}$/),
    campaign_code: z.string().regex(/^[A-Z0-9-]{3,32}$/),
    subject: z.string().min(1).max(200),
    body_text: z.string().min(1).max(10_000),
});
/** campaign_approve (v0.1.137): the exact campaign definition (definition_hash) Sercan activates. */
export const campaignApprovePayloadSchema = z.strictObject({
    campaign_code: z.string().regex(/^[A-Z0-9-]{3,32}$/),
    definition_hash: z.string().regex(/^[0-9a-f]{64}$/),
    summary_tr: textTr,
});
/**
 * autonomy_stage_raise (v0.1.203; Sercan, 18 Oct 2026: "ben onay vermeme gerek yok. atın hepsini."): the campaign's
 * autonomy stage goes up; at stage 3 its audited first mails go on the campaign's approval, without a card each.
 */
export const campaignAutonomyPayloadSchema = z
    .strictObject({
    campaign_code: z.string().regex(/^[A-Z0-9-]{3,32}$/),
    from_stage: z.number().int().min(1).max(2),
    to_stage: z.number().int().min(2).max(3),
    summary_tr: textTr,
})
    .refine((payload) => payload.to_stage > payload.from_stage, { message: 'to_stage must be above from_stage' });
export const refundPayloadSchema = z.strictObject({
    order_id: idStringSchema,
    amount_cents: z.number().int().positive(),
    reason_tr: textTr,
});
export const adSpendPayloadSchema = z.strictObject({
    channel: z.string().min(1).max(100),
    amount_cents: z.number().int().positive(),
    period_days: z.number().int().min(1).max(365),
    reason_tr: textTr,
});
/**
 * Actions without an executor in C2 (and without a detailed schema yet): a summary and
 * up to 30 flat fields the panel lists as key and value. C3 adds a specific schema
 * together with the handler.
 */
export const genericDecisionPayloadSchema = z.strictObject({
    summary_tr: textTr,
    fields: z
        .record(z.string().regex(/^[a-z][a-z0-9_]{0,39}$/), z.union([z.string().max(500), z.number(), z.boolean(), z.null()]))
        .refine((fields) => Object.keys(fields).length <= 30, { error: 'at most 30 fields' }),
});
/** Payload schema families; several action types share one (the email actions, the C3 actions). */
export const PAYLOAD_SCHEMA_KINDS = Object.freeze({
    customer_reply: customerReplyPayloadSchema,
    delivery_message: deliveryMessagePayloadSchema,
    listing_text_update: listingTextUpdatePayloadSchema,
    listing_new_draft: listingNewDraftPayloadSchema,
    listing_revert: listingRevertPayloadSchema,
    price_change: priceChangePayloadSchema,
    coupon_create: couponCreatePayloadSchema,
    site_merge: siteMergePayloadSchema,
    site_publish: sitePublishPayloadSchema,
    email: emailPayloadSchema,
    outreach_email: outreachEmailPayloadSchema,
    campaign_approve: campaignApprovePayloadSchema,
    campaign_autonomy: campaignAutonomyPayloadSchema,
    refund: refundPayloadSchema,
    ad_spend: adSpendPayloadSchema,
    generic_decision: genericDecisionPayloadSchema,
});
/**
 * approvals.preview (written by the proposer, not part of the hash, context only). The
 * panel always draws the outgoing text from the payload. policy_cap_pct can only narrow
 * the coupon cap.
 */
export const approvalPreviewSchema = z.strictObject({
    summary_tr: z.string().min(1).max(2000),
    summary_lang: z.enum(['tr', 'en']),
    quote_note_tr: z.string().max(500).optional(),
    meta_tr: z.string().max(500).optional(),
    rationale_tr: z.string().max(2000).optional(),
    impact_tr: z.string().max(1000).optional(),
    waiting_tr: z.string().max(200).optional(),
    desktop_note_tr: z.string().max(500).optional(),
    policy_cap_pct: z.number().int().min(1).max(100).optional(),
});
// ---------------------------------------------------------------------------
// Registry (5.2)
// ---------------------------------------------------------------------------
const NOT_YET = 'Yürütücü bu eylemi henüz uygulayamıyor; onaylarsan kaydedilir ve "uygulanamadı" bildirimi gelir.';
const applyNotYet = (topic, sublabel) => ({
    kind: 'generic',
    executor: 'apply',
    topic,
    label: 'Onayla',
    sublabel,
    desktopSublabel: sublabel,
    consequence: NOT_YET,
    toast: 'Onay kaydedildi; bu eylem henüz uygulanmıyor',
});
const holdNotYet = (topic, label, sublabel) => ({
    kind: 'generic',
    executor: 'hold_apply',
    topic,
    label,
    sublabel,
    desktopSublabel: sublabel,
    consequence: `${NOT_YET} 1,5 sn basılı tut.`,
    toast: 'Onay kaydedildi; bu eylem henüz uygulanmıyor',
});
const entries = [
    {
        actionType: 'customer_reply',
        handler: 'manual_copy',
        panel: {
            kind: 'customer_reply',
            executor: 'manual_copy',
            topic: 'DIŞ',
            label: 'Onayla ve kopyala',
            sublabel: "Etsy'de yapıştırıp sen gönderirsin",
            desktopSublabel: "Etsy'de sen gönderirsin",
            consequence: 'Etsy mesajlarına API ile yazamıyoruz. Onaylarsan metin kopyalanır ve Etsy mesajları açılır; gönderince işaretlersin.',
            openLabel: "Etsy'de aç",
            openUrl: 'https://www.etsy.com/messages',
        },
        editField: 'reply_en',
        schemaKind: 'customer_reply',
        implemented: true,
    },
    {
        actionType: 'delivery_message',
        handler: 'manual_copy',
        panel: {
            kind: 'customer_reply',
            executor: 'manual_copy',
            topic: 'DIŞ',
            label: 'Onayla',
            sublabel: 'İndirme linkini Twin üretir',
            desktopSublabel: 'İndirme linkini Twin üretir, sen gönderirsin',
            consequence: "Onaylayınca Twin indirme linkini Portal'da üretir (dosya başına 3 indirme, süresiz; siparişin açık linki varsa onu kullanır) ve linkli metni Diğer › Gönderilecekler'e koyar. Oradan kopyalayıp Etsy'den ya da e-postayla sen gönderirsin, sonra Gönderdim'e basarsın.",
            openLabel: 'Etsy siparişlerini aç',
            openUrl: 'https://www.etsy.com/your/orders/sold',
        },
        editField: 'reply_en',
        schemaKind: 'delivery_message',
        implemented: true,
    },
    {
        actionType: 'listing_text_update',
        handler: 'etsy_listing_update',
        panel: {
            kind: 'listing_title',
            executor: 'apply',
            topic: 'DIŞ',
            label: 'Onayla',
            sublabel: 'Etsy ilanına uygulanır · 10 sn geri alma',
            desktopSublabel: 'Etsy ilanına uygulanır',
            consequence: 'Onaylarsan yürütücü 10 sn bekler, sonra değişikliği Etsy ilanına yazar. Eski değer saklanır, geri alınabilir.',
            toast: "İlan değişikliği 10 sn sonra Etsy'ye uygulanacak",
        },
        editField: 'changes.title.after',
        schemaKind: 'listing_text_update',
        implemented: true,
    },
    {
        actionType: 'listing_new_draft',
        handler: 'manual_copy',
        panel: {
            kind: 'generic',
            executor: 'manual_copy',
            topic: 'DIŞ',
            label: 'Onayla ve kopyala',
            sublabel: "Etsy'de ilanı sen açarsın",
            desktopSublabel: "Etsy'de ilanı sen açarsın",
            consequence: "Twin Etsy'de ilan açamaz. Onaylarsan başlık, etiketler ve açıklama kopyalanır ve Diğer › Gönderilecekler'de alan alan durur; Etsy'de yeni ilan açıp (https://www.etsy.com/your/shops/me/listing-editor/create) yapıştırırsın, sonra Yaptım'a basarsın.",
            openLabel: "Etsy'de yeni ilan aç",
            openUrl: 'https://www.etsy.com/your/shops/me/listing-editor/create',
        },
        editField: 'title_en',
        schemaKind: 'listing_new_draft',
        implemented: true,
    },
    {
        actionType: 'listing_revert',
        handler: 'etsy_listing_update',
        panel: {
            kind: 'generic',
            executor: 'apply',
            topic: 'DIŞ',
            label: 'Eski hâline dön',
            sublabel: 'Önceki değer geri yazılır · 10 sn geri alma',
            desktopSublabel: 'Önceki değer geri yazılır',
            consequence: "Onaylarsan yürütücü 10 sn bekler, sonra ilanın önceki değerlerini Etsy'ye geri yazar.",
            toast: 'İlan 10 sn sonra eski hâline dönecek',
        },
        editField: null,
        schemaKind: 'listing_revert',
        implemented: true,
    },
    {
        actionType: 'price_change',
        handler: 'etsy_listing_update',
        panel: {
            kind: 'generic',
            executor: 'hold_apply',
            topic: 'PARA',
            label: 'Basılı tut: Fiyatı uygula',
            sublabel: 'Etsy fiyatı değişir · 10 sn geri alma',
            desktopSublabel: 'Etsy fiyatı değişir',
            consequence: 'Yeni fiyat Etsy ilanına yazılır. 1,5 sn basılı tut; sonra 10 sn geri alma süresi var.',
            toast: "Fiyat 10 sn sonra Etsy'de değişecek",
        },
        editField: null,
        schemaKind: 'price_change',
        implemented: true,
    },
    {
        actionType: 'coupon_create',
        handler: 'etsy_coupon',
        panel: {
            kind: 'coupon',
            executor: 'hold_apply',
            topic: 'PARA',
            label: 'Basılı tut: Kuponu yayınla',
            sublabel: 'Kupon Etsy mağazasında yayınlanır',
            desktopSublabel: 'Kupon Etsy mağazasında yayınlanır',
            consequence: 'Kupon Etsy mağazasında yayınlanır. 1,5 sn basılı tut; sonra 10 sn geri alma süresi var.',
            toast: "Kupon 10 sn sonra Etsy'de yayınlanacak",
        },
        editField: null,
        schemaKind: 'coupon_create',
        implemented: false,
    },
    {
        actionType: 'listing_media_update',
        handler: 'not_implemented',
        panel: applyNotYet('DIŞ', 'Etsy ilanına uygulanır · 10 sn geri alma'),
        editField: null,
        schemaKind: 'generic_decision',
        implemented: false,
    },
    {
        actionType: 'listing_create',
        handler: 'not_implemented',
        panel: applyNotYet('DIŞ', "Etsy'de ilan açılır · 10 sn geri alma"),
        editField: null,
        schemaKind: 'generic_decision',
        implemented: false,
    },
    {
        actionType: 'social_post',
        handler: 'not_implemented',
        panel: applyNotYet('DIŞ', 'Paylaşılır · 10 sn geri alma'),
        editField: null,
        schemaKind: 'generic_decision',
        implemented: false,
    },
    {
        actionType: 'pinterest_pin',
        handler: 'not_implemented',
        panel: applyNotYet('DIŞ', 'Pinlenir · 10 sn geri alma'),
        editField: null,
        schemaKind: 'generic_decision',
        implemented: false,
    },
    {
        actionType: 'site_merge',
        handler: 'site_pr',
        panel: {
            kind: 'generic',
            executor: 'apply',
            topic: 'DIŞ',
            label: 'Onayla ve birleştir',
            sublabel: 'PR birleştirilir, site yayına alınır',
            desktopSublabel: 'PR birleştirilir, site yayına alınır',
            consequence: "Onaylarsan yürütücü 10 sn bekler, sonra PR'ı onaylanan commit ile birleştirir. PR bu arada değiştiyse birleştirmez.",
            toast: 'PR 10 sn sonra birleştirilecek',
        },
        editField: null,
        schemaKind: 'site_merge',
        implemented: true,
    },
    {
        // v0.1.192: the SEO loop changes a guide; the site builder publishes it after the build and the SEO audit.
        actionType: 'site_publish',
        handler: 'site_publish',
        panel: {
            kind: 'generic',
            executor: 'apply',
            topic: 'DIŞ',
            label: 'Onayla ve yayınla',
            sublabel: 'Site derlenir, denetimden geçerse yayına alınır',
            desktopSublabel: 'Site derlenir, denetimden geçerse yayına alınır',
            consequence: 'Onaylarsan rehber bu değişikliklerle derlenir; SEO denetimi geçerse birkaç dakikada yayına alınır ve Google ile Bing’e bildirilir. Sayfa bu arada değiştiyse yayınlanmaz.',
            toast: 'Değişiklik yayına alınacak',
        },
        editField: null,
        schemaKind: 'site_publish',
        implemented: true,
    },
    {
        actionType: 'custom_quote',
        handler: 'email_send',
        panel: holdNotYet('PARA', 'Basılı tut: Teklifi gönder', 'E-posta ile gönderilir (Gmail C3)'),
        editField: null,
        schemaKind: 'email',
        implemented: false,
    },
    {
        actionType: 'b2b_quote',
        handler: 'email_send',
        panel: holdNotYet('PARA', 'Basılı tut: Teklifi gönder', 'Doğrudan satış teklifi (gönderim kapalı)'),
        editField: null,
        schemaKind: 'email',
        implemented: false,
    },
    {
        // v0.1.137: decided on Tanıtım gönderimi (growth approvals never reach the Onaylar queue); the
        // executor schedules the message, the send gate checks it again, the worker sends it.
        actionType: 'b2b_email',
        handler: 'outreach_schedule',
        panel: {
            kind: 'generic',
            executor: 'apply',
            topic: 'DIŞ',
            label: 'Onayla',
            sublabel: 'Gönderim kapısından geçerse sıraya girer',
            desktopSublabel: 'Gönderim kapısından geçerse sıraya girer',
            consequence: 'Onaylarsan yürütücü maili sıraya alır; gönderim kapısı her şeyi yeniden sınar, kutu günlük tavan ve alıcının iş saatleri içinde gönderir.',
            toast: 'Mail onaylandı; sıraya girecek',
        },
        editField: null,
        schemaKind: 'outreach_email',
        implemented: true,
    },
    {
        // v0.1.143: the Temsilci's answer to a firm's reply, decided on Tanıtım gönderimi like a first mail; the
        // executor schedules it, the send gate checks it again, the worker sends it in the same thread.
        actionType: 'outreach_reply',
        handler: 'outreach_schedule',
        panel: {
            kind: 'generic',
            executor: 'apply',
            topic: 'DIŞ',
            label: 'Onayla',
            sublabel: 'Aynı konuya cevap olarak sıraya girer',
            desktopSublabel: 'Aynı konuya cevap olarak sıraya girer',
            consequence: 'Onaylarsan yürütücü cevabı sıraya alır; gönderim kapısı her şeyi yeniden sınar, kutu firmanın iş saatleri içinde aynı konuya cevap olarak gönderir.',
            toast: 'Cevap onaylandı; sıraya girecek',
        },
        editField: null,
        schemaKind: 'outreach_email',
        implemented: true,
    },
    {
        actionType: 'refund',
        handler: 'manual_copy',
        panel: {
            kind: 'generic',
            executor: 'hold_apply',
            topic: 'PARA',
            label: 'Basılı tut: İadeyi onayla',
            sublabel: "Etsy'de sen yaparsın",
            desktopSublabel: "Etsy'de sen yaparsın",
            consequence: 'Yürütücü iade yapamaz. Onaylarsan iadeyi Etsy\'de sen yaparsın, sonra "Gönderdim" ile işaretlersin.',
            toast: "İade onaylandı; Etsy'de sen yapacaksın",
        },
        editField: null,
        schemaKind: 'refund',
        implemented: true,
    },
    {
        actionType: 'ad_spend',
        handler: 'manual_copy',
        panel: {
            kind: 'generic',
            executor: 'hold_apply',
            topic: 'PARA',
            label: 'Basılı tut: Harcamayı onayla',
            sublabel: 'Reklam panelinde sen yaparsın',
            desktopSublabel: 'Reklam panelinde sen yaparsın',
            consequence: 'Yürütücü reklam harcaması yapamaz. Onaylarsan harcamayı reklam panelinde sen yaparsın, sonra "Gönderdim" ile işaretlersin.',
            toast: 'Harcama onaylandı; reklam panelinde sen yapacaksın',
        },
        editField: null,
        schemaKind: 'ad_spend',
        implemented: true,
    },
    {
        actionType: 'design_delivery',
        handler: 'not_implemented',
        panel: holdNotYet('ÜRÜN', 'Basılı tut: Teslim linkini üret', 'Teslim linki üretilir'),
        editField: null,
        schemaKind: 'generic_decision',
        implemented: false,
    },
    {
        actionType: 'r2_migration',
        handler: 'not_implemented',
        panel: applyNotYet('ÜRÜN', 'Nesne taşınır ve doğrulanır'),
        editField: null,
        schemaKind: 'generic_decision',
        implemented: false,
    },
    {
        // v0.1.137: Tanıtım gönderimi activates the campaign with the approval; the executor confirms it.
        actionType: 'campaign_approve',
        handler: 'campaign_activate',
        panel: {
            kind: 'generic',
            // T3: the hold is taken on Tanıtım gönderimi before the approval (POST .../campaigns/{code}/hold).
            executor: 'apply',
            topic: 'DIŞ',
            label: 'Kampanyayı onayla',
            sublabel: 'Kampanya etkin olur',
            desktopSublabel: 'Kampanya etkin olur',
            consequence: 'Kampanya etkin olur; mailler yine tek tek senin onayınla ve gönderim kapısından geçerek gider.',
            toast: 'Kampanya onaylandı',
        },
        editField: null,
        schemaKind: 'campaign_approve',
        implemented: true,
    },
    {
        actionType: 'new_channel',
        handler: 'not_implemented',
        panel: holdNotYet('ÜRÜN', 'Basılı tut: Kanalı aç', 'Yeni satış kanalı'),
        editField: null,
        schemaKind: 'generic_decision',
        implemented: false,
    },
    {
        actionType: 'media_budget_override',
        handler: 'not_implemented',
        panel: holdNotYet('PARA', 'Basılı tut: Bütçeyi aç', 'Medya bütçesi aşılır'),
        editField: null,
        schemaKind: 'generic_decision',
        implemented: false,
    },
    {
        actionType: 'new_media_vendor',
        handler: 'not_implemented',
        panel: holdNotYet('PARA', 'Basılı tut: Sağlayıcıyı ekle', 'Yeni medya sağlayıcısı'),
        editField: null,
        schemaKind: 'generic_decision',
        implemented: false,
    },
    {
        actionType: 'customer_material_to_ai',
        handler: 'not_implemented',
        panel: holdNotYet('ÜRÜN', 'Basılı tut: İzin ver', 'Müşteri malzemesi yapay zekâya gider'),
        editField: null,
        schemaKind: 'generic_decision',
        implemented: false,
    },
    {
        // v0.1.203: at stage 3 the campaign's audited first mails go without a card each; the executor raises the stage.
        actionType: 'autonomy_stage_raise',
        handler: 'campaign_autonomy',
        panel: {
            kind: 'generic',
            executor: 'hold_apply',
            topic: 'DIŞ',
            label: 'Basılı tut: Otomatik gönderimi aç',
            sublabel: 'Mailler tek tek onaysız gider',
            desktopSublabel: 'Denetimden geçen ilk mailler tek tek onaysız gider',
            consequence: 'Kampanyanın denetimden geçmiş ilk mailleri tek tek onayın olmadan, günlük sınırla ve gönderim kapısından geçerek gider. Metin denetimin okuduğu metin olmalı. Geri dönen bir mail ya da olumsuz bir cevap gelirse otomatik gönderim durur; sıradakiler bekler, yeni mailler kart olarak gelir. 1,5 sn basılı tut.',
            toast: 'Otomatik gönderim açıldı',
        },
        editField: null,
        schemaKind: 'campaign_autonomy',
        implemented: true,
    },
];
/** The registry by action type. Every key is a twin.action_types row (checked by a DB test). */
export const ACTION_REGISTRY = Object.freeze(Object.fromEntries(entries.map(({ schemaKind: _kind, ...entry }) => [
    entry.actionType,
    Object.freeze({ ...entry, payloadSchema: entry.actionType }),
])));
/** ACTION_PAYLOAD_SCHEMAS[action_type]: the payload schema of every registered action type. */
export const ACTION_PAYLOAD_SCHEMAS = Object.freeze(Object.fromEntries(entries.map((entry) => [entry.actionType, PAYLOAD_SCHEMA_KINDS[entry.schemaKind]])));
/** The schema family of an action type (the panel draws generic_decision payloads as key and value). */
export function payloadSchemaKindOf(actionType) {
    return entries.find((entry) => entry.actionType === actionType)?.schemaKind ?? null;
}
export function actionEntry(actionType) {
    return Object.hasOwn(ACTION_REGISTRY, actionType) ? (ACTION_REGISTRY[actionType] ?? null) : null;
}
/** The payload schema of an action type, or null for an unknown type. */
export function payloadSchemaOf(actionType) {
    return Object.hasOwn(ACTION_PAYLOAD_SCHEMAS, actionType) ? (ACTION_PAYLOAD_SCHEMAS[actionType] ?? null) : null;
}
/** Validates a payload against its action's schema; issues carry field paths, never values. */
export function checkActionPayload(actionType, payload) {
    const schema = payloadSchemaOf(actionType);
    if (schema === null)
        return { ok: false, issues: [`unknown action type ${actionType}`] };
    const parsed = schema.safeParse(payload);
    if (parsed.success)
        return { ok: true, payload: parsed.data };
    return {
        ok: false,
        issues: parsed.error.issues.map((issue) => `${issue.path.join('.') || '(payload)'}: ${issue.message}`),
    };
}
/**
 * "Düzelt ve onayla" (2.5 step 10, 5.10): the new payload is the old one with only the
 * edit field replaced. customer_reply: reply_en = text, reply_tr null, edited_by_human
 * true; a URL, e-mail address or domain in the edited text is policy_violation (422,
 * 2.5 step 12), other schema failures invalid_request (400). listing_text_update: only
 * when changes holds title alone, changes.title.after = text. Every other action (and
 * every T4) has no edit.
 */
export function applyEdit(actionType, payload, editedText) {
    const entry = actionEntry(actionType);
    if (entry === null || entry.editField === null)
        return { ok: false, code: 'edit_not_allowed' };
    let next;
    if (actionType === 'customer_reply' || actionType === 'delivery_message') {
        if (payload.reply_en === editedText)
            return { ok: false, code: 'edit_unchanged' };
        if (containsLinkOrDomain(withoutOwnEtsyLinks(editedText)))
            return { ok: false, code: 'policy_violation' };
        next = { ...payload, reply_en: editedText, reply_tr: null, edited_by_human: true };
    }
    else if (actionType === 'listing_text_update') {
        const changes = payload.changes;
        if (typeof changes !== 'object' || changes === null || Array.isArray(changes)) {
            return { ok: false, code: 'edit_not_allowed' };
        }
        const keys = Object.keys(changes);
        const titleChange = changes.title;
        if (keys.length !== 1 || keys[0] !== 'title' || typeof titleChange !== 'object' || titleChange === null) {
            return { ok: false, code: 'edit_not_allowed' };
        }
        if (titleChange.after === editedText)
            return { ok: false, code: 'edit_unchanged' };
        next = { ...payload, changes: { title: { ...titleChange, after: editedText } } };
    }
    else if (actionType === 'listing_new_draft') {
        // v0.1.223: like a listing title change, the title is the field Sercan corrects before copying.
        if (payload.title_en === editedText)
            return { ok: false, code: 'edit_unchanged' };
        next = { ...payload, title_en: editedText };
    }
    else {
        return { ok: false, code: 'edit_not_allowed' };
    }
    const check = checkActionPayload(actionType, next);
    return check.ok ? { ok: true, payload: check.payload } : { ok: false, code: 'invalid_request', issues: check.issues };
}
/**
 * The coupon cap the API applies (2.5 step 12): the smallest of policies.yaml
 * pricing.discount_max_pct, system_state policy.discount_max_pct and the preview's
 * policy_cap_pct (which can only narrow it).
 */
export function couponCapPct(input) {
    return Math.min(input.yamlMaxPct, input.systemStateMaxPct, input.previewCapPct ?? Number.POSITIVE_INFINITY);
}
//# sourceMappingURL=actions.js.map